/**
 * Gift card scan popup
 * - Token activate: ?t=&store=&utm_source=giftcard
 * - Fixed coupon: ?fixed_coupon_code=NS30 (after /discount/{code} 302)
 */
(function () {
  const TOKEN_RE = /^[a-f0-9]{16,64}$/i;
  const COUPON_RE = /^[A-Za-z0-9_-]{2,32}$/;
  const STORAGE_PREFIX = 'gc_scan_shown_';
  const COUPON_DEADLINE_PREFIX = 'gc_coupon_deadline_';
  const COUPON_SEEN_PREFIX = 'gc_coupon_seen_';
  const FLOAT_OFFER_KEY = 'gc_float_offer';
  const FLOAT_OFFER_VERSION = 1;
  const COUPON_COUNTDOWN_MS = 7 * 86400 * 1000;

  function getConfig() {
    return window.geekoGiftcardScanPopupConfig || {};
  }

  function isFloatPageAllowed() {
    const body = document.body && document.body.classList;
    if (!body) return false;
    return body.contains('page-index') || body.contains('page-product') || body.contains('page-collection');
  }

  function decodeHtml(text) {
    if (text == null || text === '') return text;
    var str = String(text);
    if (str.indexOf('&') === -1) return str;
    var el = document.createElement('textarea');
    el.innerHTML = str;
    return el.value;
  }

  function getStrings() {
    var raw = getConfig().strings || {};
    if (raw.__decoded) return raw;
    Object.keys(raw).forEach(function (key) {
      raw[key] = decodeHtml(raw[key]);
    });
    raw.__decoded = true;
    return raw;
  }

  function readParams() {
    try {
      return new URLSearchParams(window.location.search);
    } catch (_) {
      return new URLSearchParams();
    }
  }

  function pad2(value) {
    return String(value).padStart(2, '0');
  }

  function pick(obj, snake, camel) {
    if (!obj) return undefined;
    if (obj[snake] != null) return obj[snake];
    if (obj[camel] != null) return obj[camel];
    return undefined;
  }

  function parseFetchJson(res) {
    return res.text().then((text) => {
      var json = null;
      if (text && text.trim()) {
        try {
          json = JSON.parse(text);
        } catch (_) {
          json = null;
        }
      }
      return { ok: res.ok, status: res.status, json: json };
    });
  }

  function resolveCouponEndpoint(config) {
    var explicit = String(config.couponApiEndpoint || '').replace(/\/$/, '');
    if (explicit) return explicit;
    var activate = String(config.apiEndpoint || '').replace(/\/$/, '');
    if (activate && /\/activate$/i.test(activate)) {
      return activate.replace(/\/activate$/i, '/coupon');
    }
    return '';
  }

  function currencySymbol(code) {
    var c = String(code || 'USD').toUpperCase();
    if (c === 'USD' || c === 'CAD' || c === 'AUD' || c === 'NZD') return '$';
    if (c === 'EUR') return '€';
    if (c === 'GBP') return '£';
    if (c === 'JPY' || c === 'CNY') return '¥';
    return c;
  }

  class GeekoGiftcardScanPopup extends HTMLElement {
    constructor() {
      super();
      this._isOpen = false;
      this._reqId = 0;
      this._timerId = null;
      this._deadlineAt = null;
      this._copyTimer = null;
      this._activatingRetryTimer = null;
      this._keepShoppingUrl = '';
      this._mode = '';
      this._couponCode = '';
      this._couponDeadlineAt = null;
      this._fromPreview = false;
      this._skipPopupTrack = false;
      this._floatExpireTimer = null;
      this._onKeydown = this._onKeydown.bind(this);
      this._onVisibility = this._onVisibility.bind(this);
    }

    connectedCallback() {
      if (this._initialized) return;
      this._initialized = true;

      const config = getConfig();
      if (config.enabled === false) return;

      this._cacheDom();
      this._bindUi();
      document.addEventListener('visibilitychange', this._onVisibility);

      // const preview = this._readPreview();
      // if (preview) {
      //   this._fromPreview = true;
      //   this._open();
      //   this._renderScene(preview.scene, preview.data);
      //   return;
      // }

      const gate = this._readGate();
      if (gate.triggered) {
        this._mode = 'token';
        this._token = gate.token;
        this._store = gate.store;

        if (!gate.validToken || !gate.validStore) {
          this._showError('invalid', false);
          this._stripScanParams();
          return;
        }

        this._activate(false);
        return;
      }

      const couponGate = this._readFixedCouponGate();
      if (couponGate.triggered) {
        this._mode = 'coupon';
        this._couponCode = couponGate.code;
        this._store = couponGate.store;
        this._queryCoupon(false);
        return;
      }

      this._restoreFloatIcon();
    }

    disconnectedCallback() {
      this._teardown();
    }

    _cacheDom() {
      this._dialog = this.querySelector('.geeko-giftcard-scan-popup__dialog');
      this._closeBtn = this.querySelector('.geeko-giftcard-scan-popup__close');
      this._panels = {
        offer: this.querySelector('[data-gc-scan-panel="offer"]'),
        expired: this.querySelector('[data-gc-scan-panel="expired"]'),
        used: this.querySelector('[data-gc-scan-panel="used"]'),
        error: this.querySelector('[data-gc-scan-panel="error"]'),
      };
      this._offerTitle = this.querySelector('[data-gc-scan-offer-title]');
      this._offerLead = this.querySelector('[data-gc-scan-offer-lead]');
      this._discountEl = this.querySelector('[data-gc-scan-discount]');
      this._scopeEl = this.querySelector('[data-gc-scan-scope]');
      this._minimumEl = this.querySelector('[data-gc-scan-minimum]');
      this._timerWrap = this.querySelector('[data-gc-scan-timer]');
      this._daysEl = this.querySelector('[data-gc-scan-days]');
      this._hrsEl = this.querySelector('[data-gc-scan-hrs]');
      this._minEl = this.querySelector('[data-gc-scan-min]');
      this._secEl = this.querySelector('[data-gc-scan-sec]');
      this._codeRow = this.querySelector('[data-gc-scan-code-row]');
      this._codeEl = this.querySelector('[data-gc-scan-code]');
      this._copyBtn = this.querySelector('[data-gc-scan-copy]');
      this._copyLabel = this.querySelector('[data-gc-scan-copy-label]');
      this._shopLink = this.querySelector('[data-gc-scan-shop]');
      this._errorMsg = this.querySelector('[data-gc-scan-error-msg]');
      this._errorCta = this.querySelector('[data-gc-scan-error-cta]');
      this._floatBtn = this.querySelector('[data-gc-float-icon]');
      this._floatBg = this.querySelector('.geeko-giftcard-scan-popup__float-bg');
      this._floatMain = this.querySelector('[data-gc-float-main]');
      this._floatOff = this.querySelector('[data-gc-float-off]');
      const floatIconUrl = String(getConfig().floatIconUrl || '').trim();
      if (this._floatBg && floatIconUrl) {
        this._floatBg.setAttribute('src', floatIconUrl);
      }
    }

    _bindUi() {
      this.querySelectorAll('[data-gc-scan-close]').forEach((el) => {
        el.addEventListener('click', (event) => {
          event.preventDefault();
          this.close();
        });
      });

      this.querySelectorAll('[data-gc-scan-keep]').forEach((el) => {
        el.addEventListener('click', (event) => {
          event.preventDefault();
          this._goKeepShopping();
        });
      });

      this._shopLink?.addEventListener('click', (event) => {
        const href = this._shopLink.getAttribute('href') || '';
        if (!href || href === '#') {
          event.preventDefault();
          this.close();
        }
      });

      this._copyBtn?.addEventListener('click', (event) => {
        event.preventDefault();
        this._copyCode();
      });

      this._errorCta?.addEventListener('click', (event) => {
        event.preventDefault();
        if (this._errorCta.dataset.retry === 'true') {
          if (this._mode === 'coupon') this._queryCoupon(false);
          else this._activate(false);
          return;
        }
        this._goKeepShopping();
      });

      this._floatBtn?.addEventListener('click', (event) => {
        event.preventDefault();
        this._openFromFloat();
      });
    }

    _readPreview() {
      const raw = String(readParams().get('gc_preview') || '').trim().toUpperCase();
      if (!raw) return null;

      const mock = {
        coupon_code: 'NS30',
        discount_text: '30% OFF EVERYTHING',
        discount_type: 'percentage',
        discount_value: 30,
        no_minimum: true,
        remaining_seconds: 7 * 86400,
        first_activate: raw !== 'STILL_ACTIVE',
      };

      if (raw === 'EXPIRED' || raw === 'USED') {
        return { scene: raw, data: mock };
      }
      if (raw === 'STILL_ACTIVE') {
        return { scene: 'STILL_ACTIVE', data: mock };
      }
      return { scene: 'FIRST_ACTIVATE', data: mock };
    }

    _readGate() {
      const params = readParams();
      const token = String(params.get('t') || '').trim();
      const storeRaw = String(params.get('store') || '').trim();
      const utm = String(params.get('utm_source') || '').trim().toLowerCase();
      const store = Number(storeRaw);
      const allowed = (getConfig().allowedStores || [1818, 1819]).map(Number);

      return {
        triggered: Boolean(token && storeRaw && utm === 'giftcard'),
        token: token,
        store: store,
        validToken: TOKEN_RE.test(token),
        validStore: allowed.indexOf(store) !== -1,
      };
    }

    _readFixedCouponGate() {
      const params = readParams();
      const code = String(params.get('fixed_coupon_code') || '').trim();
      if (!code) return { triggered: false };
      if (code.toLowerCase() === 'none' || !COUPON_RE.test(code)) {
        return { triggered: false };
      }

      const config = getConfig();
      const allowed = (config.allowedStores || [1818, 1819]).map(Number);
      const defaultStore = Number(config.defaultStoreId || 1818);
      const storeRaw = String(params.get('store') || '').trim();
      var store = defaultStore;
      if (storeRaw) {
        const parsed = Number(storeRaw);
        if (allowed.indexOf(parsed) !== -1) store = parsed;
      } else if (allowed.indexOf(defaultStore) === -1 && allowed.length) {
        store = allowed[0];
      }

      return { triggered: true, code: code, store: store };
    }

    _open() {
      this._hideFloatIcon();
      if (this._isOpen) return;
      this._isOpen = true;
      this.removeAttribute('hidden');
      this.setAttribute('aria-hidden', 'false');
      this.classList.add('is-open');
      document.documentElement.setAttribute('data-gc-scan-open', 'true');
      document.body.classList.add('overflow-hidden', 'geeko-giftcard-scan-popup-open');
      document.addEventListener('keydown', this._onKeydown, true);
      this._closeBtn?.focus();
    }

    close() {
      if (!this._isOpen) return;
      this._reqId += 1;
      this._stopTimer();
      if (this._activatingRetryTimer) {
        clearTimeout(this._activatingRetryTimer);
        this._activatingRetryTimer = null;
      }
      this._stripScanParams();
      this._stripFixedCouponParam();
      this._markShown();
      this._isOpen = false;
      this.removeAttribute('hidden');
      this.setAttribute('aria-hidden', 'false');
      this.classList.remove('is-open', 'is-scene-simple', 'is-scene-offer');
      document.documentElement.removeAttribute('data-gc-scan-open');
      document.body.classList.remove('overflow-hidden', 'geeko-giftcard-scan-popup-open');
      document.removeEventListener('keydown', this._onKeydown, true);
      this._showFloatIcon();
    }

    _teardown() {
      this._stopTimer();
      this._clearFloatExpireTimer();
      document.removeEventListener('keydown', this._onKeydown, true);
      document.removeEventListener('visibilitychange', this._onVisibility);
    }

    _onKeydown(event) {
      if (!this._isOpen) return;
      if (event.key === 'Escape') {
        event.preventDefault();
        event.stopPropagation();
        this.close();
      }
    }

    _onVisibility() {
      if (document.visibilityState === 'visible') {
        this._tickTimer();
        this._syncFloatOfferExpiry();
      }
    }

    _showPanel(name) {
      Object.keys(this._panels).forEach((key) => {
        const panel = this._panels[key];
        if (!panel) return;
        panel.hidden = key !== name;
      });
      const simple = name !== 'offer';
      this.classList.toggle('is-scene-simple', simple);
      this.classList.toggle('is-scene-offer', !simple);
    }

    _activate(isAutoRetry) {
      const config = getConfig();
      const endpoint = String(config.apiEndpoint || '').replace(/\/$/, '');
      if (!endpoint) {
        this._showError('generic', true);
        this._stripScanParams();
        return;
      }

      this._reqId += 1;
      const reqId = this._reqId;

      fetch(endpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
        body: JSON.stringify({
          shopify_store_account_id: this._store,
          token: this._token,
        }),
      })
        .then(parseFetchJson)
        .then((payload) => {
          if (reqId !== this._reqId) return;
          this._handleResponse(payload, isAutoRetry);
        })
        .catch(() => {
          if (reqId !== this._reqId) return;
          this._showError('network', true);
          this._stripScanParams();
        });
    }

    _handleResponse(payload, isAutoRetry) {
      const json = payload.json;
      if (!json) {
        this._showError('generic', true);
        this._stripScanParams();
        return;
      }

      const result = json.result != null ? json.result : json.data;
      const success =
        json.success === true ||
        Number(json.code) === 0 ||
        Number(json.code) === 200;

      if (success && result && typeof result === 'object') {
        const scene = this._resolveScene(result);
        if (scene === 'ERROR_UNAVAILABLE') {
          this._showError('unavailable', false);
        } else {
          this._renderScene(scene, result);
        }
        this._stripScanParams();
        this._markShown();
        return;
      }

      const code = Number(json.code);
      if (code === 40903 && !isAutoRetry) {
        const retryReqId = this._reqId;
        this._activatingRetryTimer = setTimeout(() => {
          this._activatingRetryTimer = null;
          if (this._reqId !== retryReqId) return;
          this._activate(true);
        }, 1500);
        return;
      }

      this._showError(this._mapErrorKey(code), this._canRetry(code) || !payload.ok);
      this._stripScanParams();
    }

    _queryCoupon(isRetry) {
      const config = getConfig();
      const endpoint = resolveCouponEndpoint(config);
      if (!endpoint || !this._couponCode) {
        this._showError('generic', true);
        return;
      }

      this._reqId += 1;
      const reqId = this._reqId;

      const url =
        endpoint +
        (endpoint.indexOf('?') === -1 ? '?' : '&') +
        'shopify_store_account_id=' +
        encodeURIComponent(String(this._store)) +
        '&coupon_code=' +
        encodeURIComponent(this._couponCode);

      fetch(url, {
        method: 'GET',
        headers: { Accept: 'application/json' },
      })
        .then(parseFetchJson)
        .then((payload) => {
          if (reqId !== this._reqId) return;
          this._handleCouponResponse(payload, isRetry);
        })
        .catch(() => {
          if (reqId !== this._reqId) return;
          if (!isRetry) {
            this._scheduleCouponRetry();
            return;
          }
          this._showError('network', true);
        });
    }

    _scheduleCouponRetry() {
      if (this._activatingRetryTimer) {
        clearTimeout(this._activatingRetryTimer);
        this._activatingRetryTimer = null;
      }
      const retryReqId = this._reqId;
      this._activatingRetryTimer = setTimeout(() => {
        this._activatingRetryTimer = null;
        if (retryReqId !== this._reqId) return;
        this._queryCoupon(true);
      }, 1500);
    }

    _handleCouponResponse(payload, isRetry) {
      const json = payload.json;
      if (!json) {
        if (!isRetry) {
          this._scheduleCouponRetry();
          return;
        }
        this._showError('generic', true);
        return;
      }

      const result = json.result != null ? json.result : json.data;
      const success =
        json.success === true ||
        Number(json.code) === 0 ||
        Number(json.code) === 200;

      if (success && result && typeof result === 'object') {
        if (!pick(result, 'coupon_code', 'couponCode') && this._couponCode) {
          result.coupon_code = this._couponCode;
        }
        const scene = this._resolveScene(result);
        if (scene === 'ERROR_UNAVAILABLE') {
          this._showError('unavailable', false);
        } else {
          this._renderScene(scene, result);
        }
        this._stripFixedCouponParam();
        return;
      }

      const code = Number(json.code);
      const canRetry = this._canRetryCoupon(code) || !payload.ok;
      if (canRetry && !isRetry) {
        this._scheduleCouponRetry();
        return;
      }
      this._showError(this._mapCouponErrorKey(code), canRetry);
      if (!canRetry) this._stripFixedCouponParam();
    }

    _resolveScene(result) {
      var scene = String(pick(result, 'display_scene', 'displayScene') || '').toUpperCase();
      if (scene === 'SCHEDULED' || scene === 'DISABLED') return 'ERROR_UNAVAILABLE';
      if (
        scene !== 'FIRST_ACTIVATE' &&
        scene !== 'STILL_ACTIVE' &&
        scene !== 'EXPIRED' &&
        scene !== 'USED'
      ) {
        if (result.used === true) scene = 'USED';
        else if (result.expired === true) scene = 'EXPIRED';
        else if (result.first_activate === true || result.firstActivate === true) scene = 'FIRST_ACTIVATE';
        else if (result.usable === true) scene = 'STILL_ACTIVE';
        else if (pick(result, 'coupon_code', 'couponCode')) scene = 'STILL_ACTIVE';
        else scene = 'EXPIRED';
      }

      if (this._mode === 'coupon' && (scene === 'STILL_ACTIVE' || scene === 'FIRST_ACTIVATE')) {
        scene = this._hasSeenCoupon(this._couponCode) ? 'STILL_ACTIVE' : 'FIRST_ACTIVATE';
      }

      if (this._mode !== 'coupon' && (scene === 'FIRST_ACTIVATE' || scene === 'STILL_ACTIVE')) {
        const remaining = this._getRemainingSeconds(result);
        const hasEnd =
          pick(result, 'expires_at', 'expiresAt') || pick(result, 'ends_at', 'endsAt');
        if (remaining === 0 && hasEnd) return 'EXPIRED';
      }
      return scene;
    }

    _renderScene(scene, result) {
      this._open();
      if (scene === 'EXPIRED') {
        this._stopTimer();
        this._keepShoppingUrl = this._resolveKeepShoppingUrl(result);
        this._showPanel('expired');
        this._clearFloatOffer();
        return;
      }
      if (scene === 'USED') {
        this._stopTimer();
        this._keepShoppingUrl = this._resolveKeepShoppingUrl(result);
        this._showPanel('used');
        this._clearFloatOffer();
        return;
      }

      const strings = getStrings();
      const first = scene === 'FIRST_ACTIVATE';
      if (this._offerTitle) {
        this._offerTitle.textContent = first
          ? strings.congrats_title || ''
          : strings.welcome_title || '';
      }
      if (this._offerLead) {
        this._offerLead.textContent = first
          ? strings.congrats_lead || ''
          : strings.welcome_lead || '';
      }
      const offer = this._splitDiscount(this._formatDiscount(result), result);
      if (this._discountEl) this._discountEl.textContent = offer.main;
      if (this._scopeEl) this._scopeEl.textContent = offer.scope;
      if (this._minimumEl) this._minimumEl.textContent = this._formatMinimum(result);

      const code = String(
        pick(result, 'coupon_code', 'couponCode') || this._couponCode || ''
      );
      if (this._codeEl) this._codeEl.textContent = code;
      if (this._codeRow) this._codeRow.hidden = !code;
      if (this._copyLabel) this._copyLabel.textContent = strings.copy || 'Copy';

      if (this._shopLink) {
        this._shopLink.setAttribute('href', this._resolveShopNowUrl(result, code));
      }

      this._keepShoppingUrl = this._resolveKeepShoppingUrl(result);
      this._showPanel('offer');
      this._startTimer(result);
      if (this._mode === 'coupon') this._markCouponSeen(this._couponCode);
      this._saveFloatOffer(result);
      this._trackScanPopup(scene, code);
    }

    _formatDiscount(result) {
      const strings = getStrings();
      const ready = pick(result, 'discount_text', 'discountText');
      if (ready) return String(ready);

      const type = pick(result, 'discount_type', 'discountType');
      const value = pick(result, 'discount_value', 'discountValue');
      const currency = pick(result, 'currency', 'currency') || 'USD';
      if (value == null || value === '') return '';

      if (type === 'percentage') {
        return (strings.discount_percentage || '__VALUE__% OFF').replace(
          /__VALUE__/g,
          String(value)
        );
      }
      if (type === 'fixed_amount') {
        return (strings.discount_fixed || '__CURRENCY____VALUE__ OFF')
          .replace(/__CURRENCY__/g, String(currency))
          .replace(/__VALUE__/g, String(value));
      }
      return String(value);
    }

    _splitDiscount(text, result) {
      const strings = getStrings();
      const raw = String(text || '').trim();
      if (!raw) return { main: '', scope: '' };
      const match = raw.match(/^(.*?\bOFF)\s+(.*)$/i);
      if (match) return { main: match[1], scope: match[2] };
      const appliesAll = !result || (result.applies_to_all !== false && result.appliesToAll !== false);
      return {
        main: raw,
        scope: appliesAll ? strings.off_everything || 'EVERYTHING' : '',
      };
    }

    _formatMinimum(result) {
      const strings = getStrings();
      const noMin = result.no_minimum === true || result.noMinimum === true;
      if (noMin) return strings.no_minimum || 'No Minimum';

      const subtotal = pick(result, 'minimum_subtotal', 'minimumSubtotal');
      if (subtotal) {
        return (strings.minimum_subtotal || 'Min. __VALUE__').replace(/__VALUE__/g, String(subtotal));
      }
      const qty = pick(result, 'minimum_quantity', 'minimumQuantity');
      if (qty) {
        return (strings.minimum_quantity || 'Min. __VALUE__ items').replace(/__VALUE__/g, String(qty));
      }
      return '';
    }

    _couponDeadlineKey(code) {
      return COUPON_DEADLINE_PREFIX + String(code || '').trim().toUpperCase();
    }

    _couponSeenKey(code) {
      return COUPON_SEEN_PREFIX + String(code || '').trim().toUpperCase();
    }

    _hasSeenCoupon(code) {
      if (!code) return false;
      try {
        return localStorage.getItem(this._couponSeenKey(code)) === '1';
      } catch (_) {
        return false;
      }
    }

    _markCouponSeen(code) {
      if (!code) return;
      try {
        localStorage.setItem(this._couponSeenKey(code), '1');
      } catch (_) {
        /* ignore */
      }
    }

    _readCouponDeadline(code) {
      if (!code) return null;
      try {
        const n = Number(localStorage.getItem(this._couponDeadlineKey(code)));
        if (Number.isFinite(n) && n > 0) return n;
      } catch (_) {
        /* ignore */
      }
      return null;
    }

    _writeCouponDeadline(code, deadline) {
      if (!code || !deadline) return;
      try {
        localStorage.setItem(this._couponDeadlineKey(code), String(deadline));
      } catch (_) {
        /* ignore */
      }
    }

    _ensureCouponDeadline(code) {
      const now = Date.now();
      var deadline = this._readCouponDeadline(code);
      if (!deadline || deadline <= now) {
        deadline = now + COUPON_COUNTDOWN_MS;
        this._writeCouponDeadline(code, deadline);
      }
      this._couponDeadlineAt = deadline;
      return deadline;
    }

    _getCouponCycleRemainingSeconds() {
      const code = this._couponCode;
      var deadline = this._couponDeadlineAt;
      if (!deadline || deadline <= Date.now()) {
        deadline = this._ensureCouponDeadline(code);
      }
      return Math.max(1, Math.floor((deadline - Date.now()) / 1000));
    }

    _getRemainingSeconds(result) {
      if (this._mode === 'coupon') {
        this._ensureCouponDeadline(this._couponCode);
        return this._getCouponCycleRemainingSeconds();
      }
      const expires =
        pick(result, 'expires_at', 'expiresAt') || pick(result, 'ends_at', 'endsAt');
      const remaining = pick(result, 'remaining_seconds', 'remainingSeconds');
      if (remaining != null && remaining !== '') {
        const n = Number(remaining);
        if (Number.isFinite(n)) {
          if (n <= 0 && !expires) return null;
          return Math.max(0, Math.floor(n));
        }
      }
      if (expires) {
        const t = new Date(expires).getTime();
        if (!Number.isNaN(t)) return Math.max(0, Math.floor((t - Date.now()) / 1000));
      }
      return null;
    }

    _startTimer(result) {
      this._stopTimer();
      if (this._mode === 'coupon') {
        this._ensureCouponDeadline(this._couponCode);
        if (this._timerWrap) this._timerWrap.hidden = false;
        this._tickTimer();
        this._timerId = window.setInterval(() => this._tickTimer(), 1000);
        return;
      }
      const remaining = this._getRemainingSeconds(result);
      if (remaining == null || remaining <= 0) {
        if (this._timerWrap) this._timerWrap.hidden = true;
        return;
      }
      if (this._timerWrap) this._timerWrap.hidden = false;
      this._deadlineAt = Date.now() + remaining * 1000;
      this._tickTimer();
      this._timerId = window.setInterval(() => this._tickTimer(), 1000);
    }

    _tickTimer() {
      var left;
      if (this._mode === 'coupon' && this._couponCode) {
        left = this._getCouponCycleRemainingSeconds();
      } else {
        if (this._deadlineAt == null) return;
        left = Math.max(0, Math.floor((this._deadlineAt - Date.now()) / 1000));
      }
      const days = Math.floor(left / 86400);
      const hrs = Math.floor((left % 86400) / 3600);
      const mins = Math.floor((left % 3600) / 60);
      const secs = left % 60;
      if (this._daysEl) this._daysEl.textContent = pad2(days);
      if (this._hrsEl) this._hrsEl.textContent = pad2(hrs);
      if (this._minEl) this._minEl.textContent = pad2(mins);
      if (this._secEl) this._secEl.textContent = pad2(secs);
      if (this._mode !== 'coupon' && left <= 0) {
        this._stopTimer();
        this._showPanel('expired');
        this._clearFloatOffer();
      }
    }

    _stopTimer() {
      if (this._timerId) {
        clearInterval(this._timerId);
        this._timerId = null;
      }
      this._deadlineAt = null;
    }

    _resolveShopNowUrl(result, code) {
      const config = getConfig();
      const fromApi = pick(result, 'shop_now_url', 'shopNowUrl');
      if (fromApi) return String(fromApi);
      if (this._mode === 'coupon') {
        if (config.shopNowUrl) return String(config.shopNowUrl);
        return '#';
      }
      if (config.applyDiscountOnShopNow !== false && code) {
        const root = config.rootUrl || '/';
        return '/discount/' + encodeURIComponent(code) + '?redirect=' + encodeURIComponent(root);
      }
      if (config.shopNowUrl) return String(config.shopNowUrl);
      return config.rootUrl || '/';
    }

    _resolveKeepShoppingUrl(result) {
      const fromApi = result ? pick(result, 'keep_shopping_url', 'keepShoppingUrl') : '';
      if (fromApi) return String(fromApi);
      const config = getConfig();
      if (config.keepShoppingUrl) return String(config.keepShoppingUrl);
      return '';
    }

    _goKeepShopping() {
      const url = this._keepShoppingUrl;
      this.close();
      if (url) window.location.assign(url);
    }

    _copyCode() {
      const text = (this._codeEl && this._codeEl.textContent) || '';
      if (!text) return;
      const strings = getStrings();
      const done = () => {
        if (!this._copyLabel) return;
        this._copyLabel.textContent = strings.copied || 'Copied';
        if (this._copyTimer) clearTimeout(this._copyTimer);
        this._copyTimer = setTimeout(() => {
          this._copyLabel.textContent = strings.copy || 'Copy';
        }, 1600);
      };
      if (navigator.clipboard && navigator.clipboard.writeText) {
        navigator.clipboard.writeText(text).then(done).catch(() => this._fallbackCopy(text, done));
      } else {
        this._fallbackCopy(text, done);
      }
    }

    _fallbackCopy(text, done) {
      try {
        const ta = document.createElement('textarea');
        ta.value = text;
        ta.setAttribute('readonly', '');
        ta.style.position = 'absolute';
        ta.style.left = '-9999px';
        document.body.appendChild(ta);
        ta.select();
        document.execCommand('copy');
        document.body.removeChild(ta);
        if (done) done();
      } catch (_) {
        /* ignore */
      }
    }

    _mapErrorKey(code) {
      if (code === 40401) return 'not_found';
      if (code === 40901) return 'void';
      if (code === 40903) return 'activating';
      if (code === 50001 || code === 500) return 'shopify';
      return 'generic';
    }

    _mapCouponErrorKey(code) {
      if (code === 40402) return 'coupon_not_found';
      if (code === 50001 || code === 500) return 'generic';
      return 'generic';
    }

    _canRetry(code) {
      return code === 40903 || code === 50001 || code === 500;
    }

    _canRetryCoupon(code) {
      return code === 50001 || code === 500 || code >= 500;
    }

    _showError(key, canRetry) {
      this._open();
      const strings = getStrings();
      const map = {
        not_found: strings.error_not_found,
        void: strings.error_void,
        activating: strings.error_activating,
        shopify: strings.error_shopify,
        network: strings.error_network,
        invalid: strings.error_invalid_link,
        unavailable: strings.error_unavailable,
        coupon_not_found: strings.error_coupon_not_found,
        generic: strings.error_generic,
      };
      if (this._errorMsg) this._errorMsg.textContent = map[key] || strings.error_generic || '';
      if (this._errorCta) {
        this._errorCta.dataset.retry = canRetry ? 'true' : 'false';
        this._errorCta.textContent = canRetry
          ? strings.try_again || 'TRY AGAIN'
          : strings.keep_shopping || 'KEEP SHOPPING';
      }
      this._keepShoppingUrl = this._resolveKeepShoppingUrl(null);
      this._showPanel('error');
      if (key === 'unavailable') this._clearFloatOffer();
    }

    _stripScanParams() {
      try {
        const params = readParams();
        if (!params.has('t') && !params.has('store')) return;
        params.delete('t');
        params.delete('store');
        const qs = params.toString();
        const next =
          window.location.pathname + (qs ? '?' + qs : '') + window.location.hash;
        History.prototype.replaceState.call(history, {}, '', next);
      } catch (_) {
        /* ignore */
      }
    }

    _stripFixedCouponParam() {
      try {
        const params = readParams();
        if (!params.has('fixed_coupon_code')) return;
        params.delete('fixed_coupon_code');
        const qs = params.toString();
        const next =
          window.location.pathname + (qs ? '?' + qs : '') + window.location.hash;
        History.prototype.replaceState.call(history, {}, '', next);
      } catch (_) {
        /* ignore */
      }
    }

    _markShown() {
      if (!this._token) return;
      try {
        sessionStorage.setItem(STORAGE_PREFIX + this._token, '1');
      } catch (_) {
        /* ignore */
      }
    }

    _splitFloatDiscount(offer) {
      const strings = getStrings();
      const offLabel = strings.float_off || 'OFF';
      const type = String(offer && offer.discount_type ? offer.discount_type : '').toLowerCase();
      const value = offer ? offer.discount_value : null;
      if (type === 'percentage' && value != null && value !== '') {
        return { main: String(value) + '%', off: offLabel };
      }
      if (type === 'fixed_amount' && value != null && value !== '') {
        return { main: currencySymbol(offer.currency) + String(value), off: offLabel };
      }
      const text = String((offer && offer.discount_text) || '').trim();
      const match = text.match(/^(.+?)\s+OFF\b/i);
      if (match) return { main: match[1].trim(), off: offLabel };
      if (text) {
        return { main: text.length > 8 ? text.slice(0, 8) : text, off: '' };
      }
      return { main: '', off: '' };
    }

    _snapshotToResult(offer) {
      var remaining = null;
      var endsAt = null;
      if (offer.deadline_at) {
        remaining = Math.max(0, Math.floor((Number(offer.deadline_at) - Date.now()) / 1000));
        if (remaining === 0 && Number(offer.deadline_at) > Date.now()) remaining = 1;
        endsAt = new Date(Number(offer.deadline_at)).toISOString();
      }
      return {
        coupon_code: offer.coupon_code,
        discount_text: offer.discount_text,
        discount_type: offer.discount_type,
        discount_value: offer.discount_value,
        currency: offer.currency,
        applies_to_all: offer.applies_to_all,
        no_minimum: offer.no_minimum,
        minimum_subtotal: offer.minimum_subtotal,
        minimum_quantity: offer.minimum_quantity,
        remaining_seconds: remaining,
        ends_at: endsAt,
        expires_at: endsAt,
      };
    }

    _isFloatOfferValid(offer) {
      if (!offer || offer.v !== FLOAT_OFFER_VERSION) return false;
      if (!offer.coupon_code) return false;
      if (offer.mode !== 'coupon' && offer.mode !== 'token') return false;
      if (offer.mode === 'token' && offer.deadline_at && Number(offer.deadline_at) <= Date.now()) {
        return false;
      }
      return true;
    }

    _readFloatOffer() {
      try {
        const raw = localStorage.getItem(FLOAT_OFFER_KEY);
        if (!raw) return null;
        const offer = JSON.parse(raw);
        if (!this._isFloatOfferValid(offer)) {
          this._clearFloatOffer();
          return null;
        }
        return offer;
      } catch (_) {
        this._clearFloatOffer();
        return null;
      }
    }

    _writeFloatOffer(offer) {
      try {
        localStorage.setItem(FLOAT_OFFER_KEY, JSON.stringify(offer));
      } catch (_) {
        /* ignore */
      }
    }

    _saveFloatOffer(result) {
      if (this._fromPreview) return;
      const code = String(
        pick(result, 'coupon_code', 'couponCode') || this._couponCode || ''
      ).trim();
      if (!code) return;

      var deadlineAt = null;
      if (this._mode === 'token') {
        if (this._deadlineAt) deadlineAt = this._deadlineAt;
        else {
          const remaining = pick(result, 'remaining_seconds', 'remainingSeconds');
          const expires =
            pick(result, 'expires_at', 'expiresAt') || pick(result, 'ends_at', 'endsAt');
          if (remaining != null && remaining !== '') {
            const n = Number(remaining);
            if (Number.isFinite(n) && n > 0) deadlineAt = Date.now() + n * 1000;
          } else if (expires) {
            const t = new Date(expires).getTime();
            if (!Number.isNaN(t) && t > Date.now()) deadlineAt = t;
          }
        }
        if (deadlineAt && deadlineAt <= Date.now()) {
          this._clearFloatOffer();
          return;
        }
      } else if (this._mode === 'coupon') {
        deadlineAt = this._couponDeadlineAt || this._readCouponDeadline(code);
      }

      if (this._mode === 'token' && !deadlineAt) {
        try {
          const prev = JSON.parse(localStorage.getItem(FLOAT_OFFER_KEY) || 'null');
          if (prev && prev.coupon_code === code && prev.deadline_at) {
            deadlineAt = Number(prev.deadline_at);
          }
        } catch (_) {
          /* ignore */
        }
      }
      if (this._mode === 'token' && deadlineAt && deadlineAt <= Date.now()) {
        this._clearFloatOffer();
        return;
      }

      const offer = {
        v: FLOAT_OFFER_VERSION,
        mode: this._mode === 'coupon' ? 'coupon' : 'token',
        coupon_code: code,
        store: Number(this._store || getConfig().defaultStoreId || 1818),
        discount_text: String(pick(result, 'discount_text', 'discountText') || this._formatDiscount(result) || ''),
        discount_type: pick(result, 'discount_type', 'discountType') || null,
        discount_value: pick(result, 'discount_value', 'discountValue') != null
          ? pick(result, 'discount_value', 'discountValue')
          : null,
        currency: pick(result, 'currency', 'currency') || 'USD',
        applies_to_all: result.applies_to_all !== false && result.appliesToAll !== false,
        no_minimum: result.no_minimum === true || result.noMinimum === true,
        minimum_subtotal: pick(result, 'minimum_subtotal', 'minimumSubtotal') || null,
        minimum_quantity: pick(result, 'minimum_quantity', 'minimumQuantity') || null,
        deadline_at: deadlineAt || null,
        updated_at: Date.now(),
      };
      this._writeFloatOffer(offer);
      this._paintFloatIcon(offer);
    }

    _clearFloatOffer() {
      if (this._fromPreview) return;
      this._clearFloatExpireTimer();
      try {
        localStorage.removeItem(FLOAT_OFFER_KEY);
      } catch (_) {
        /* ignore */
      }
      this._hideFloatIcon();
    }

    _clearFloatExpireTimer() {
      if (this._floatExpireTimer) {
        clearTimeout(this._floatExpireTimer);
        this._floatExpireTimer = null;
      }
    }

    _scheduleFloatExpiry(offer) {
      this._clearFloatExpireTimer();
      if (!offer || offer.mode !== 'token' || !offer.deadline_at) return;
      const left = Number(offer.deadline_at) - Date.now();
      if (left <= 0) {
        this._clearFloatOffer();
        return;
      }
      this._floatExpireTimer = setTimeout(() => {
        this._floatExpireTimer = null;
        this._syncFloatOfferExpiry();
      }, Math.min(left, 2147483647));
    }

    _syncFloatOfferExpiry() {
      const offer = this._readFloatOffer();
      if (!offer) {
        this._hideFloatIcon();
        return;
      }
      this._scheduleFloatExpiry(offer);
    }

    _paintFloatIcon(offer) {
      if (!this._floatBtn || !offer) return;
      const parts = this._splitFloatDiscount(offer);
      if (this._floatMain) this._floatMain.textContent = parts.main;
      if (this._floatOff) this._floatOff.textContent = parts.off || '';
      const strings = getStrings();
      const label = String(offer.discount_text || [parts.main, parts.off].filter(Boolean).join(' ')).trim();
      const aria = (strings.float_aria || 'Your __VALUE__ offer').replace(/__VALUE__/g, label || 'gift card');
      this._floatBtn.setAttribute('aria-label', aria);
    }

    _hideFloatIcon() {
      if (this._floatBtn) this._floatBtn.hidden = true;
    }

    _showFloatIcon() {
      if (this._isOpen || !isFloatPageAllowed()) {
        this._hideFloatIcon();
        return;
      }
      const offer = this._readFloatOffer();
      if (!offer) {
        this._hideFloatIcon();
        return;
      }
      this._paintFloatIcon(offer);
      this._scheduleFloatExpiry(offer);
      if (this._floatBtn) this._floatBtn.hidden = false;
    }

    _restoreFloatIcon() {
      this._showFloatIcon();
    }

    _openFromFloat() {
      const offer = this._readFloatOffer();
      if (!offer) return;
      this._mode = offer.mode;
      this._couponCode = offer.coupon_code;
      this._store = offer.store;
      if (offer.mode === 'coupon') {
        this._ensureCouponDeadline(offer.coupon_code);
      }
      this._open();
      this._skipPopupTrack = true;
      this._renderScene('STILL_ACTIVE', this._snapshotToResult(offer));
      this._skipPopupTrack = false;
    }

    _trackScanPopup(scene, couponCode) {
      if (this._fromPreview || this._skipPopupTrack) return;
      if (scene !== 'FIRST_ACTIVATE' && scene !== 'STILL_ACTIVE') return;
      const code = String(couponCode || '').trim();
      if (!code) return;

      this._publish('geeko_gifcard_popup', {
        coupon_code: code,
        scene: scene,
        link_type: this._mode === 'coupon' ? 'fixed_coupon' : 'giftcard',
      });
    }

    _publish(name, payload) {
      if (typeof Shopify !== 'undefined' && Shopify.analytics?.publish) {
        Shopify.analytics.publish(name, payload);
      }
    }
  }

  if (!customElements.get('geeko-giftcard-scan-popup')) {
    customElements.define('geeko-giftcard-scan-popup', GeekoGiftcardScanPopup);
  }
})();
